import {ReactNode, useMemo} from 'react';

import {
  MessagePartHandlers,
  MessagePartHandlersProvider,
} from '@stripe-internal/conversations-ui';

import type {MessagePartClient} from '../types';

type Props = {
  client: MessagePartClient | undefined;
  children: ReactNode;
};

/**
 * Derives the message-part handlers from an assistant client and publishes them
 * on MessagePartHandlersContext. Shared by the StripeAssistant and Threads
 * provider trees, which differ only in how they get hold of the client.
 *
 * Clients that don't implement the message-part RPCs (e.g. UniversalHelpClient)
 * yield empty handlers, leaving consumers on their own fallback path.
 */
export function MessagePartHandlersFromClient({client, children}: Props) {
  const handlers = useMemo<MessagePartHandlers>(() => {
    if (!client?.actOnMessagePart || !client?.submitMessagePart) {
      return {};
    }

    return {
      onAction: client.actOnMessagePart,
      onSubmit: client.submitMessagePart,
    };
  }, [client]);

  return (
    <MessagePartHandlersProvider value={handlers}>
      {children}
    </MessagePartHandlersProvider>
  );
}
